import React from 'react';
import { FormProvider } from 'react-hook-form';

import { ReadOnlySankeyChart } from 'components/SankeyChart';
import Button from 'ui/Button';
import Dialog from 'ui/Dialog';
import Icon from 'ui/Icon';
import { VStack } from 'ui/Layout';
import { Text } from 'ui/Typography';
import { useCurrentFormSankeyId } from 'utils/form';

import { useSankeyExcelBindingContext } from '../../contexts/sankeyExcelBindingContext';
import SankeyRenderProvider from '../SankeyRenderProvider';
import CellsSelectionInfo from './CellsSelectionInfo';
import {
  useCreateFromExcelMutation,
  useDraftFormBasedOnCellsSelection,
} from './utils';

type Props = {
  open: boolean;
  onClose: () => void;
  isCreating: boolean;
};

// TODO: Rename to ExcelCellsSourceDialog and integrate with opener trigger
const ExcelCellsSourceModal: React.FC<Props> = ({
  open,
  onClose,
  isCreating,
}) => {
  const {
    bindingAddress,
    selectedAddress,
    isRangeSelectionValid,
    bindDiagramToCurrentExcelSelection,
  } = useSankeyExcelBindingContext();

  const sankeyId = useCurrentFormSankeyId() ?? -1; // TODO: Move away from -1 fallback

  const draftForm = useDraftFormBasedOnCellsSelection(isCreating);
  const createMutation = useCreateFromExcelMutation(draftForm);

  const title = isCreating
    ? isRangeSelectionValid
      ? 'Draft from your selection'
      : 'Select cells in your spreadsheet'
    : 'Select new cells';

  return (
    <Dialog.Root open={open} onOpenChange={onClose} size="xs">
      <Dialog.Content>
        <Dialog.Header>
          <Icon fontSize="md" icon="table-cells" mr={2} /> {title}
        </Dialog.Header>
        <Dialog.Body>
          <FormProvider {...draftForm}>
            <VStack alignItems="stretch" gap={4}>
              {!isRangeSelectionValid ? (
                <>
                  <Text color="gray.600" fontSize="sm">
                    Please make sure that your selection consists of 3-4 columns
                    formatted as in the following example (the last column is
                    optional):
                  </Text>
                  <img
                    src={require('../../assets/excel-selection.gif')}
                    alt="Excel selection"
                  />
                </>
              ) : (
                <>
                  <Text color="gray.600" fontSize="sm">
                    You will be able to modify your cells' data and keep
                    refining the diagram after you create it.
                  </Text>
                  <SankeyRenderProvider>
                    <ReadOnlySankeyChart />
                  </SankeyRenderProvider>
                </>
              )}
              <CellsSelectionInfo isCreating={isCreating} sankeyId={sankeyId} />
            </VStack>
          </FormProvider>
        </Dialog.Body>
        <Dialog.Footer>
          {isCreating ? (
            <Button
              variant="primary-solid"
              onClick={async () => {
                await createMutation.mutateAsync();
                onClose();
              }}
              disabled={!isRangeSelectionValid}
              loading={createMutation.isLoading}
            >
              Create and refine
            </Button>
          ) : (
            <Button
              variant="primary-solid"
              onClick={async () => {
                await bindDiagramToCurrentExcelSelection(sankeyId);
                onClose();
              }}
              disabled={
                selectedAddress === bindingAddress || !isRangeSelectionValid
              }
            >
              Apply
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ExcelCellsSourceModal;
